import { popupsTasks } from '@gem/popups';
import { CLIPBOARD_SUPPORTED, copyTextToClipboardFallback } from '@gem/utils';

async function getAndCopyText<TResponse>(
  method: () => Promise<TResponse>,
  adapter: (response: TResponse) => string
): Promise<void> {
  const response = await method();
  const text = adapter(response);
  copyTextToClipboardFallback(text);
}

async function getAndCopyTextThroughClipboardItem<TResponse>(
  method: () => Promise<TResponse>,
  adapter: (response: TResponse) => string
): Promise<void> {
  const text = new ClipboardItem({
    'text/plain': method()
      .then(adapter)
      .then((text) => new Blob([text], { type: 'text/plain' })),
  });
  await window.navigator.clipboard.write([text]);
}

interface IAsyncCopyToClipboardParams<TResponse> {
  method: () => Promise<TResponse>;
  adapter: (response: TResponse) => string;
  successMessage: string;
  errorMessage: string;
}

// Safari does not allow asynchronous copying to the clipboard,
// so the ClipboardItem interface is used for this action (see "getAndCopyTextThroughClipboardItem" func)
export async function asyncCopyToClipboard<TResponse>({
  method,
  adapter,
  successMessage,
  errorMessage,
}: IAsyncCopyToClipboardParams<TResponse>): Promise<void> {
  try {
    if (CLIPBOARD_SUPPORTED) {
      await getAndCopyTextThroughClipboardItem(method, adapter);
    } else {
      await getAndCopyText(method, adapter);
    }
    popupsTasks.showToast(successMessage);
  } catch {
    popupsTasks.showToast(errorMessage);
  }
}
